import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { supabase, isSupabaseConfigured } from "../../lib/supabaseClient";
import ROUTES from "../../router/routesModel";
import { takeIntendedDestination } from "../../router/intendedDestination";
import {
  setTokenInLocalStorage,
  getUserFromLocalStorage,
} from "../service/localStorage";
import { syncSupabaseToApp } from "../service/userApi";
import { NAME_AT_SIGN, NO_EMAIL_AS_NAME, nameLongEnough } from "../helpers/publicName";
import { useUser } from "../providers/UserProvider";
import { useSnack } from "../../providers/SnackBarProvider";
import {
  loadAuthTrace,
  mergeAuthTrace,
  appendAuthStep,
  probeAuthStorage,
  buildSafeAuthDebugReport,
  sanitizeExchangeError,
  traceIsoNow,
} from "../../lib/authTrace";
import { claimRefusals } from "../../utils/refusalNotice";
import { dvh } from "../../styles/viewportHeight";

/**
 * PKCE / OAuth completion errors often surface when verifier state is missing
 * (different browser, cleared storage, refreshed callback, stale links).
 */
const isLikelyPkceFlowError = (text = "") =>
  /flow state|invalid flow|pkce/i.test(String(text));

/**
 * Shown to the USER, not to a developer. `finalizeFailure` puts this in
 * `setMessage`, which renders in the top-level Typography below — outside
 * `SafeAuthDebugDetails` and ungated by `showFailureUI`. Keep it to actions a
 * locked-out tester can actually take: the "(PKCE session)" parenthetical and
 * the "confirm Supabase Authentication redirect URLs" sentence were both
 * removed for that reason (p1-pres-07-MORE-1) — the latter needs a Supabase
 * dashboard. Note this also reaches setSnack at the same call site, so the copy
 * appears in a snackbar too.
 *
 * This does NOT put the vendor's name out of the user's sight, and nothing here
 * should be read as claiming it does. `SafeAuthDebugDetails` below still renders
 * unconditionally with the failure UI; what changed (p1-pres-07-MORE-3a) is that
 * its Accordion no longer carries `defaultExpanded`, so "Supabase configured
 * (OAuth start)" is behind one tap instead of open on arrival. Re-labelling those
 * rows in user language is option (b) of the same item and is still open.
 *
 * Two vendor/protocol leaks into THIS ungated Typography survive both changes and
 * are the reason p1-pres-07-MORE-1 is still not closed: `sanitizeExchangeError`
 * strips URLs, `code=` and token names but not "PKCE"/"Supabase", so the two
 * call sites that interpolate a sanitized provider message ahead of this copy can
 * reintroduce the deleted sentence from the provider side (p1-pres-07-MORE-2);
 * and the "OAuth redirect error" call site emits the provider's raw code verbatim
 * into this Typography and the snackbar (p1-pres-07-MORE-4). Described by call
 * site rather than by line number on purpose — the cites in this docblock have
 * rotted twice already.
 */
const PKCE_HELP_COPY =
  "Complete sign-in in the same browser where you started. Do not reuse old links, refresh this page, or open the link on another device.";

// Owner decision Q-21 (2026-10-05). GoTrue's code for an email link (password
// reset, sign-up confirmation) that has expired or was already used. It comes
// in error_code; error=access_denied ALONE is also what a cancelled Google
// consent sends, so that alone is never read as expiry.
const EXPIRED_LINK_CODE = "otp_expired";
const EXPIRED_LINK_COPY =
  "This link has expired or was already used. Each email link works only once — send yourself a new one.";

const cleanAuthCallbackUrlBar = () => {
  try {
    const pathOnly = `${window.location.origin}${ROUTES.AUTH_CALLBACK}`;
    window.history.replaceState(null, document.title, pathOnly);
  } catch {
    /* ignore */
  }
};

/** Debug panel renders only booleans/sanitized strings from trace — no tokens/codes/full URLs */
const SafeAuthDebugDetails = ({ trace }) => {
  const t = trace || loadAuthTrace() || {};
  const [copyLabel, setCopyLabel] = React.useState("Copy safe auth debug report");

  const flowIdDisplay = typeof t.authFlowId === "string" ? t.authFlowId : "missing";
  const steps = Array.isArray(t.steps) ? t.steps : [];

  const items = [
    ["Flow id", flowIdDisplay],
    ["Provider", String(t.provider ?? "—")],
    ["Supabase configured (OAuth start)", String(t.hasSupabaseConfig ?? "—")],
    ["Redirect target (no secrets)", String(t.redirectTo ?? "—")],
    ["Callback origin", String(t.callbackOrigin ?? "—")],
    ["Callback pathname", String(t.callbackPathname ?? "—")],
    ["Has OAuth code param", String(t.hasCodeParam ?? "—")],
    ["Has OAuth error param", String(t.hasErrorParam ?? "—")],
    ["OAuth error code (short)", String(t.oauthErrorCode ?? "—")],
    ["Has URL hash", String(t.hasHash ?? "—")],
    ["Recovery fragment", String(t.isRecoveryRedirect ?? "—")],
    ["Session before exchange", String(t.sessionBeforeExchange ?? "—")],
    ["Exchange attempted", String(t.exchangeStarted ?? "—")],
    ["Exchange succeeded", String(t.exchangeSucceeded ?? "—")],
    ["Exchange failed", String(t.exchangeFailed ?? "—")],
    ["Duplicate exchange skipped", String(t.duplicateExchangeSkipped ?? "—")],
    ["Session after failed exchange", String(t.sessionAfterFailedExchange ?? "—")],
    ["Bridge started", String(t.bridgeStarted ?? "—")],
    ["Bridge succeeded", String(t.bridgeSucceeded ?? "—")],
    ["Bridge failed", String(t.bridgeFailed ?? "—")],
    ["Bridge HTTP status", String(t.bridgeHttpStatus ?? "—")],
    ["Sanitized error", String(t.exchangeErrorMessage ?? "—")],
    ["Callback loaded", String(t.callbackLoaded ?? "—")],
    ["OAuth started", String(t.startedAt ?? "—")],
  ];

  const handleCopyReport = async () => {
    try {
      await navigator.clipboard.writeText(buildSafeAuthDebugReport());
      setCopyLabel("Copied!");
      setTimeout(() => setCopyLabel("Copy safe auth debug report"), 2000);
    } catch {
      setCopyLabel("Copy failed");
      setTimeout(() => setCopyLabel("Copy safe auth debug report"), 2000);
    }
  };

  // Collapsed by default (p1-pres-07-MORE-3a): this panel renders for every auth
  // failure, so leaving it open put "Supabase configured (OAuth start)" in front
  // of a locked-out tester who only wanted to sign in. The rows stay available
  // for support — one tap. Re-labelling them in user language is a separate,
  // still-open decision (option b on the same row).
  return (
    <Accordion sx={{ width: "100%", maxWidth: 560, bgcolor: "#111827", color: "#e5e7eb" }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#94a3b8" }} />}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Auth debug details (no secrets)
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box component="ul" sx={{ m: 0, pl: 2, typography: "body2", color: "#94a3b8" }}>
          {items.map(([k, v]) => (
            <li key={k} style={{ marginBottom: "0.35rem" }}>
              <strong style={{ color: "#cbd5e1" }}>{k}:</strong> {v}
            </li>
          ))}
        </Box>
        {steps.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" sx={{ color: "#cbd5e1", fontWeight: 600, display: "block", mb: 1 }}>
              Ordered steps
            </Typography>
            <Box component="ol" sx={{ m: 0, pl: 2.5, typography: "caption", color: "#94a3b8" }}>
              {steps.map((step, idx) => (
                <li key={`${step.event}-${step.at}-${idx}`} style={{ marginBottom: "0.25rem" }}>
                  <strong style={{ color: "#cbd5e1" }}>{step.event}</strong>
                  {step.at ? ` @ ${step.at}` : ""}
                  {step.error ? ` — ${step.error}` : ""}
                  {step.httpStatus != null ? ` (HTTP ${step.httpStatus})` : ""}
                  {step.hasSession != null ? ` (session=${String(step.hasSession)})` : ""}
                </li>
              ))}
            </Box>
          </Box>
        )}
        <Button
          variant="outlined"
          size="small"
          onClick={handleCopyReport}
          sx={{ mt: 2, textTransform: "none", color: "#cbd5e1", borderColor: "#475569" }}
        >
          {copyLabel}
        </Button>
        <Typography variant="caption" sx={{ display: "block", mt: 2, opacity: 0.75 }}>
          For support screenshots: expand this panel. Never share passwords or confirmation links from email.
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
};

const AuthCallbackPage = () => {
  const navigate = useNavigate();
  const { setToken, setUser } = useUser();
  const setSnack = useSnack();
  const [message, setMessage] = useState("Signing you in…");
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [updating, setUpdating] = useState(false);
  const [resetDone, setResetDone] = useState(false);
  /** Callback UX: failures stay on page with manual login control */
  const [showFailureUI, setShowFailureUI] = useState(false);
  /**
   * OAuth providers do not give us a date of birth, and the beta is 18+. When
   * the bridge reports that a NEW account needs one, we hold the verified
   * Supabase session here and ask before completing registration. Existing
   * accounts never reach this — the gate applies to account creation only.
   */
  const [ageGateNeeded, setAgeGateNeeded] = useState(false);
  const [pendingSession, setPendingSession] = useState(null);
  const [ageDob, setAgeDob] = useState("");
  // The name other collectors will see (P1-6). Prefilled from the sign-in
  // provider's profile name when it has one — never from the email.
  const [ageFirst, setAgeFirst] = useState("");
  const [ageLast, setAgeLast] = useState("");
  const [ageError, setAgeError] = useState("");
  const [ageSubmitting, setAgeSubmitting] = useState(false);
  /**
   * When the backend rejects the Supabase→app bridge with 403
   * "email_not_confirmed", we surface a Resend button. Holds the email we
   * pulled from the Supabase session so the resend call knows who it's for.
   * null means: no email-verification issue detected.
   */
  const [emailVerifyNeeded, setEmailVerifyNeeded] = useState(null);
  const [resendState, setResendState] = useState("idle"); // idle | sending | sent | error
  // Q-21: the failure was an expired or used email link; offer a new one.
  const [linkExpired, setLinkExpired] = useState(false);
  const exchangeAttemptedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const finalizeFailure = (userMessage, extraTrace = {}) => {
      mergeAuthTrace(extraTrace);
      if (cancelled) return;
      setMessage(userMessage);
      setSnack("error", sanitizeExchangeError(userMessage));
      setShowFailureUI(true);
    };

    const tryBridgeFromSession = async (sessionObj, dateOfBirth) => {
      if (!sessionObj?.access_token) return false;
      mergeAuthTrace({ bridgeStarted: traceIsoNow() });
      appendAuthStep("bridgeStarted");
      try {
        const appToken = await syncSupabaseToApp(
          sessionObj.access_token,
          dateOfBirth
        );
        mergeAuthTrace({ bridgeSucceeded: traceIsoNow() });
        appendAuthStep("bridgeSucceeded");
        if (cancelled) return true;
        setTokenInLocalStorage(appToken);
        setToken(appToken);
        setUser(getUserFromLocalStorage());
        setSnack("success", "Signed in successfully!");
        // [#77]. Land on the page RequireAuth bounced this visitor away from,
        // exactly as the password path already does. MAP stays the fallback
        // for everyone who reached sign-in on their own.
        navigate(takeIntendedDestination() || ROUTES.MAP, { replace: true });
        return true;
      } catch (err) {
        // Every outcome below is shown on this page: the date of birth step,
        // the resend offer, or finalizeFailure's own message (B-31).
        claimRefusals();
        mergeAuthTrace({
          bridgeFailed: traceIsoNow(),
          exchangeErrorMessage: sanitizeExchangeError(err),
        });
        appendAuthStep("bridgeFailed", { error: sanitizeExchangeError(err) });
        // Recognize the 403 email-not-verified rejection so we can offer a
        // Resend button instead of leaving the user stranded on a raw error.
        // The backend message is a known literal (see
        // usersRestController.js SUPABASE_SESSION_EMAIL_UNCONFIRMED).
        const errText = typeof err === "string" ? err : err?.message || "";

        // The bridge needs a date of birth before it will create the account.
        // This is not a sign-in failure and must not be shown as one — the user
        // has done nothing wrong, we simply never asked. Hold the session and
        // render the age step; the bridge is retried with the answer.
        if (err?.code === "DOB_REQUIRED") {
          if (!cancelled) {
            setPendingSession(sessionObj);
            setAgeFirst(err.suggestedName?.first || "");
            setAgeLast(err.suggestedName?.last || "");
            setAgeGateNeeded(true);
            setMessage("");
          }
          return true;
        }

        // The account rule refuses this address (userApi.js
        // EmailNotAcceptedError). The generic line below says "Try again",
        // which is false here: nothing about the address changes on a retry.
        // The server's sentence says what to do instead, so it is shown as is.
        if (err?.code === "EMAIL_NOT_ACCEPTED") {
          finalizeFailure(err.message);
          return true;
        }

        if (/verify your email/i.test(errText)) {
          const emailFromSession = sessionObj?.user?.email || null;
          if (!cancelled) setEmailVerifyNeeded(emailFromSession);
          finalizeFailure(
            "Your email isn't verified yet. Check your inbox (and spam folder) for our confirmation link — or resend it below."
          );
        } else {
          finalizeFailure("Could not complete sign-in with the server. Try again.");
        }
        return true;
      }
    };

    // supabase-js exchangeCodeForSession(authCode) sends `auth_code` verbatim to
    // /token — it does NOT parse a URL. Passing window.location.href makes GoTrue
    // look up a flow_state keyed by the whole URL → "flow_state_not_found". Pass
    // the bare ?code= value, exactly like supabase-js's own detectSessionInUrl path.
    const runExchangeOnce = async () => {
      const authCode = new URLSearchParams(window.location.search).get("code");
      return supabase.auth.exchangeCodeForSession(authCode);
    };

    const run = async () => {
      if (!isSupabaseConfigured() || !supabase) {
        setSnack("warning", "Authentication is not configured. Please log in manually.");
        navigate(ROUTES.LOGIN, { replace: true });
        return;
      }

      mergeAuthTrace({ callbackLoaded: traceIsoNow() });
      appendAuthStep("callbackLoaded");
      const storageProbe = probeAuthStorage();
      mergeAuthTrace({
        storageProbe,
        callbackOrigin: window.location.origin,
        callbackPathname: window.location.pathname || ROUTES.AUTH_CALLBACK,
      });
      appendAuthStep("storageProbed", {
        localStorageWritable: storageProbe.localStorageWritable,
        sessionStorageWritable: storageProbe.sessionStorageWritable,
        pkceStorageKeyPresent: storageProbe.pkceStorageKeyPresent,
      });

      const searchParams = new URLSearchParams(window.location.search);
      const hasCodeParam = Boolean(searchParams.get("code"));
      const hash = window.location.hash || "";
      const hashParams = new URLSearchParams(hash.replace(/^#/, "?"));
      // Errors are read from BOTH halves of the URL. GoTrue can put them after
      // the #, and reading only the query let such a link fall through and,
      // with a session already in this browser, sign the person in (Q-21).
      const errorCode = searchParams.get("error") || hashParams.get("error") || "";
      const errorSubCode = searchParams.get("error_code") || hashParams.get("error_code") || "";
      const errorDesc = searchParams.get("error_description") || hashParams.get("error_description");
      const hasHashFlag = hash.length > 1;
      // Read `type` from BOTH halves of the URL. A `type=recovery` FRAGMENT is an
      // implicit-flow artefact; this client is flowType:"pkce" with
      // detectSessionInUrl:false (lib/supabaseClient.js), so a recovery link
      // arrives as `?code=…` with no fragment at all. Deriving recovery from the
      // hash alone made it false on the one path that ships, so every reset link
      // silently signed the user in instead of showing the set-password form.
      const isRecoveryRedirect =
        searchParams.get("type") === "recovery" || hashParams.get("type") === "recovery";
      const hasErrorParam = Boolean(errorCode || errorSubCode || errorDesc);

      mergeAuthTrace({
        hasCodeParam,
        hasErrorParam,
        hasHash: hasHashFlag,
        isRecoveryRedirect,
      });

      if (hasErrorParam) {
        const expired =
          errorSubCode === EXPIRED_LINK_CODE || /invalid or has expired/i.test(errorDesc || "");
        if (expired && !cancelled) setLinkExpired(true);
        finalizeFailure(expired ? EXPIRED_LINK_COPY : `OAuth redirect error: ${errorCode || "unknown"}`, {
          exchangeFailed: traceIsoNow(),
          oauthErrorCode: [errorCode, errorSubCode].filter(Boolean).join(" / ").slice(0, 80),
          exchangeErrorMessage: sanitizeExchangeError(errorDesc || errorCode || "oauth_error"),
        });
        return;
      }

      const { data: preData } = await supabase.auth.getSession();
      if (cancelled) return;
      const sessionBeforeExchange = !!preData.session;
      mergeAuthTrace({ sessionBeforeExchange });
      appendAuthStep("getSessionBeforeExchange", { hasSession: sessionBeforeExchange });

      // No PKCE authorization code — existing session OR recovery fragment OR stale landing
      if (!hasCodeParam) {
        if (preData.session) {
          cleanAuthCallbackUrlBar();
          // A recovery landing that already has a session is still a recovery.
          // Bridging here would mint the app JWT and navigate to /map, which is
          // the whole defect: the user asked to change their password and got
          // signed in with the old one instead.
          if (isRecoveryRedirect) {
            mergeAuthTrace({
              exchangeSucceeded: traceIsoNow(),
              reuseNote: "recovery_session_without_code_query",
            });
            setMessage("Set your new password below.");
            setRecoveryMode(true);
            return;
          }
          mergeAuthTrace({
            exchangeSucceeded: traceIsoNow(),
            reuseNote: "session_without_code_query",
          });
          await tryBridgeFromSession(preData.session);
          return;
        }

        // Password recovery emails may rely on exchanging the fragment URL once
        if (isRecoveryRedirect) {
          if (exchangeAttemptedRef.current) {
            mergeAuthTrace({
              duplicateExchangeSkipped: traceIsoNow(),
              exchangeErrorMessage: "duplicate_exchange_blocked",
            });
            finalizeFailure(
              `Stale or repeated recovery link. Request a new reset email.${` ${PKCE_HELP_COPY}`}`
            );
            return;
          }
          exchangeAttemptedRef.current = true;
          mergeAuthTrace({ exchangeStarted: traceIsoNow() });
          appendAuthStep("exchangeStarted");

          const { data: exData, error: exErr } = await runExchangeOnce();
          if (cancelled) return;

          if (exErr || !exData.session?.access_token) {
            const rawMsg = typeof exErr === "string" ? exErr : exErr?.message || "Recovery exchange failed.";
            mergeAuthTrace({
              exchangeFailed: traceIsoNow(),
              exchangeErrorMessage: sanitizeExchangeError(rawMsg),
            });
            if (isLikelyPkceFlowError(rawMsg)) {
              const { data: afterData } = await supabase.auth.getSession();
              if (cancelled) return;
              mergeAuthTrace({ sessionAfterFailedExchange: !!afterData.session });
              if (afterData.session && isRecoveryRedirect) {
                mergeAuthTrace({
                  exchangeSucceeded: traceIsoNow(),
                  recoveryFallbackAfterPkceFail: traceIsoNow(),
                });
                cleanAuthCallbackUrlBar();
                setMessage("Set your new password below.");
                setRecoveryMode(true);
                return;
              }
            }

            const { data: sessAfterRecoverEx } = await supabase.auth.getSession();
            if (cancelled) return;
            mergeAuthTrace({
              sessionAfterFailedExchange: !!sessAfterRecoverEx.session,
            });
            finalizeFailure(`${sanitizeExchangeError(rawMsg)} ${PKCE_HELP_COPY}`);
            return;
          }

          mergeAuthTrace({ exchangeSucceeded: traceIsoNow() });
          appendAuthStep("exchangeSucceeded");
          cleanAuthCallbackUrlBar();
          setMessage("Set your new password below.");
          setRecoveryMode(true);
          return;
        }

        mergeAuthTrace({
          staleCallbackLand: true,
          exchangeFailed: traceIsoNow(),
          exchangeErrorMessage: "no_code_no_session",
        });
        finalizeFailure(`This sign-in link is incomplete or expired.${` ${PKCE_HELP_COPY}`}`);
        return;
      }

      // PKCE authorization code present
      if (exchangeAttemptedRef.current) {
        mergeAuthTrace({
          duplicateExchangeSkipped: traceIsoNow(),
          exchangeErrorMessage: "duplicate_exchange_blocked",
        });
        const { data: dupData } = await supabase.auth.getSession();
        if (cancelled) return;
        mergeAuthTrace({ sessionAfterFailedExchange: !!dupData.session });
        if (dupData.session) {
          mergeAuthTrace({ reuseNote: "post_duplicate_getSession_bridge" });
          cleanAuthCallbackUrlBar();
          await tryBridgeFromSession(dupData.session);
          return;
        }
        finalizeFailure(`This page was already processed. Try signing in again.${` ${PKCE_HELP_COPY}`}`);
        return;
      }
      exchangeAttemptedRef.current = true;
      mergeAuthTrace({ exchangeStarted: traceIsoNow() });
      appendAuthStep("exchangeStarted");

      const { data: exOk, error: exchErr } = await runExchangeOnce();
      if (cancelled) return;

      if (!exchErr && exOk?.session?.access_token) {
        mergeAuthTrace({ exchangeSucceeded: traceIsoNow() });
        appendAuthStep("exchangeSucceeded");
        cleanAuthCallbackUrlBar();

        if (isRecoveryRedirect) {
          setMessage("Set your new password below.");
          setRecoveryMode(true);
          return;
        }

        const okBridge = await tryBridgeFromSession(exOk.session);
        if (!okBridge) return;
        return;
      }

      const rawFail =
        typeof exchErr === "string" ? exchErr : exchErr?.message || "Sign-in exchange failed.";
      mergeAuthTrace({
        exchangeFailed: traceIsoNow(),
        exchangeErrorMessage: sanitizeExchangeError(rawFail),
      });
      appendAuthStep("exchangeFailed", { error: sanitizeExchangeError(rawFail) });

      const { data: afterExFail } = await supabase.auth.getSession();
      if (cancelled) return;
      mergeAuthTrace({ sessionAfterFailedExchange: !!afterExFail.session });

      if (isLikelyPkceFlowError(rawFail) && afterExFail.session) {
        mergeAuthTrace({
          reuseNote: "getSession_after_invalid_flow_pkce",
        });
        cleanAuthCallbackUrlBar();
        if (!(await tryBridgeFromSession(afterExFail.session))) return;
        return;
      }

      finalizeFailure(`${sanitizeExchangeError(rawFail)} ${PKCE_HELP_COPY}`, {});
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, setToken, setUser, setSnack]);

  /**
   * Ask Supabase to resend the sign-up verification email. Uses Supabase's
   * own auth API directly (not our /users/resend-verification, which requires
   * a valid app JWT the user doesn't have yet at this point in the flow).
   */
  const handleResendVerification = async () => {
    if (!emailVerifyNeeded || !isSupabaseConfigured() || !supabase) return;
    setResendState("sending");
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: emailVerifyNeeded,
      });
      if (error) throw error;
      setResendState("sent");
      setSnack("success", "Verification email sent. Check your inbox.");
    } catch (err) {
      setResendState("error");
      setSnack("error", sanitizeExchangeError(err) || "Could not resend verification email.");
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 7) {
      setSnack("error", "Password must be at least 7 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      setSnack("error", "Passwords do not match");
      return;
    }
    // #108. `-` is inside the class, matching the "One special character
    // (!@#$%^&*-)" line UserForm.jsx shows at signup and the three Joi rules
    // (signupSchema.js, registerValidation.js, loginValidation.js) that already
    // accepted it. Without it this branch fired the snackbar below on a password
    // the app itself had called valid.
    //
    // On the `-`: it is a literal when it sits FIRST, LAST, or escaped, so
    // moving it to the front of this class would accept the identical nine
    // characters. It is kept LAST because APPENDING is the silent failure —
    // add one more character after the `-` and it becomes a range operator.
    // Measured: this class with `?` appended still compiles and accepts 29
    // characters, every digit among them. passwordSpecialCharParity.test.js
    // asserts every class ends in `-]`, which is what catches that.
    //
    // That suite reads this class LITERAL out of the source text and compiles
    // the literal on its own; it executes none of the eight rules, so it cannot
    // see a validator that mangles the password before testing it. Behaviour
    // here is covered by AuthCallbackPage.hyphenPassword.test.jsx.
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*-])/;
    if (!passwordRegex.test(newPassword)) {
      setSnack("error", "Password must contain uppercase, lowercase, number, and special character");
      return;
    }

    setUpdating(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error.message;
      setResetDone(true);
      setSnack("success", "Password updated! Redirecting to login…");
      await supabase.auth.signOut();
      setTimeout(() => navigate(ROUTES.LOGIN, { replace: true }), 2000);
    } catch (err) {
      const msg = typeof err === "string" ? err : err?.message || "Failed to update password";
      setSnack("error", msg);
    } finally {
      setUpdating(false);
    }
  };

  const handleAgeGateSubmit = async (e) => {
    e.preventDefault();
    setAgeError("");

    const first = ageFirst.trim();
    const last = ageLast.trim();
    // One character is enough in Chinese, Japanese or Korean (owner decision Q-13).
    if (!nameLongEnough(first) || !nameLongEnough(last)) {
      setAgeError("Enter the first and last name other collectors will see (at least 2 letters each).");
      return;
    }
    if (NAME_AT_SIGN.test(first) || NAME_AT_SIGN.test(last)) {
      setAgeError(NO_EMAIL_AS_NAME);
      return;
    }
    if (!ageDob) {
      setAgeError("Please enter your date of birth.");
      return;
    }

    setAgeSubmitting(true);
    try {
      const appToken = await syncSupabaseToApp(
        pendingSession?.access_token,
        ageDob,
        { first, last }
      );
      setTokenInLocalStorage(appToken);
      setToken(appToken);
      setUser(getUserFromLocalStorage());
      setSnack("success", "Signed in successfully!");
      // [#77]. Same landing rule as the direct bridge above — the age gate is
      // a detour through the same sign-in, not a different destination.
      navigate(takeIntendedDestination() || ROUTES.MAP, { replace: true });
    } catch (err) {
      // Shown on the age step as `ageError` (B-31).
      claimRefusals();
      const msg = typeof err === "string" ? err : err?.message || "Could not complete sign-in.";
      // A rejection here is the 18+ refusal, which is a final answer, not a
      // retry prompt. Keep the message as the server phrased it.
      setAgeError(msg);
    } finally {
      setAgeSubmitting(false);
    }
  };

  if (ageGateNeeded) {
    const maxDob = new Date(
      new Date().setFullYear(new Date().getFullYear() - 18)
    )
      .toISOString()
      .split("T")[0];

    return (
      <Box
        sx={{
          minHeight: dvh(100),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          px: 2,
        }}
      >
        <Box
          component="form"
          onSubmit={handleAgeGateSubmit}
          sx={{
            width: "100%",
            maxWidth: 440,
            bgcolor: "#fff",
            borderRadius: "20px",
            p: 4,
          }}
        >
          <Typography variant="h5" fontWeight={800} gutterBottom>
            One more thing
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Choose the name other collectors will see on the map, in trades and
            in chat. Tradeloft is also for collectors aged 18 and over, and your
            sign-in provider doesn&apos;t share your date of birth — please
            confirm it to finish creating your account.
          </Typography>

          <TextField
            fullWidth
            required
            label="First name"
            value={ageFirst}
            onChange={(e) => setAgeFirst(e.target.value)}
            inputProps={{ maxLength: 256, "data-testid": "age-gate-first", autoComplete: "given-name", autoCorrect: "off", spellCheck: false }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            required
            label="Last name"
            value={ageLast}
            onChange={(e) => setAgeLast(e.target.value)}
            inputProps={{ maxLength: 256, "data-testid": "age-gate-last", autoComplete: "family-name", autoCorrect: "off", spellCheck: false }}
            helperText="Shown to other collectors. You can change it later in Settings. Don't use your email address."
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            required
            type="date"
            label="Date of birth"
            value={ageDob}
            onChange={(e) => setAgeDob(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: maxDob, "data-testid": "age-gate-dob" }}
            error={Boolean(ageError)}
            helperText={ageError || " "}
            sx={{ mb: 2 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={ageSubmitting}
            sx={{ py: 1.4, borderRadius: "14px", fontWeight: 700 }}
          >
            {ageSubmitting ? "Checking…" : "Continue"}
          </Button>

          <Button
            fullWidth
            sx={{ mt: 1 }}
            onClick={() => navigate(ROUTES.LOGIN, { replace: true })}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    );
  }

  if (recoveryMode) {
    return (
      <Box
        sx={{
          minHeight: dvh(100),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 440, px: 2 }}>
          <Box
            sx={{
              bgcolor: "rgba(255,255,255,0.95)",
              borderRadius: 4,
              overflow: "hidden",
              boxShadow: "0 8px 32px rgba(31,38,135,0.37)",
            }}
          >
            <Box
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "#fff",
                py: 4,
                px: 3,
                textAlign: "center",
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                {resetDone ? "Password Updated!" : "Set New Password"}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {resetDone
                  ? "Redirecting to login…"
                  : "Choose a strong password for your account"}
              </Typography>
            </Box>
            <Box component="form" onSubmit={handlePasswordReset} sx={{ p: 3 }}>
              {resetDone ? (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <CircularProgress size={28} sx={{ color: "#7c3aed" }} />
                </Box>
              ) : (
                <>
                  <input
                    type="password"
                    placeholder="New password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    disabled={updating}
                    style={{
                      width: "100%",
                      padding: "14px 16px",
                      borderRadius: 12,
                      border: "1px solid rgba(0,0,0,0.15)",
                      fontSize: 15,
                      fontFamily: "Inter, sans-serif",
                      marginBottom: 12,
                      boxSizing: "border-box",
                      outline: "none",
                    }}
                  />
                  <input
                    type="password"
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    disabled={updating}
                    style={{
                      width: "100%",
                      padding: "14px 16px",
                      borderRadius: 12,
                      border: "1px solid rgba(0,0,0,0.15)",
                      fontSize: 15,
                      fontFamily: "Inter, sans-serif",
                      marginBottom: 8,
                      boxSizing: "border-box",
                      outline: "none",
                    }}
                  />
                  <Typography sx={{ fontSize: "0.72rem", color: "#64748b", mb: 2, px: 0.5 }}>
                    Min 7 chars, uppercase, lowercase, number, and special character
                  </Typography>
                  <button
                    type="submit"
                    disabled={updating || !newPassword || !confirmPassword}
                    style={{
                      width: "100%",
                      padding: "14px",
                      borderRadius: 12,
                      border: "none",
                      background: "linear-gradient(135deg, #667eea, #764ba2)",
                      color: "#fff",
                      fontSize: 16,
                      fontWeight: 700,
                      fontFamily: "Inter, sans-serif",
                      cursor: updating ? "default" : "pointer",
                      opacity: updating ? 0.7 : 1,
                    }}
                  >
                    {updating ? "Updating…" : "Update Password"}
                  </button>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: dvh(100),
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 2,
        bgcolor: "#0a0a0f",
        color: "#e2e8f0",
        py: 4,
        px: 2,
      }}
    >
      {!showFailureUI && <CircularProgress sx={{ color: "#7c3aed", mt: 4 }} />}
      <Typography variant="body2" sx={{ color: "#94a3b8", textAlign: "center", px: 2, maxWidth: 560 }}>
        {message}
      </Typography>

      {showFailureUI && (
        <>
          {emailVerifyNeeded && (
            <Button
              variant="contained"
              disabled={resendState === "sending" || resendState === "sent"}
              onClick={handleResendVerification}
              sx={{
                background: "linear-gradient(135deg, #22c55e, #16a34a)",
                textTransform: "none",
                fontWeight: 700,
                px: 3,
              }}
            >
              {resendState === "sending"
                ? "Sending…"
                : resendState === "sent"
                ? "Email sent — check your inbox"
                : `Resend verification email${emailVerifyNeeded ? ` to ${emailVerifyNeeded}` : ""}`}
            </Button>
          )}
          {linkExpired && (
            <Button
              variant="contained"
              onClick={() => navigate(ROUTES.FORGOT_PASSWORD, { replace: true })}
              sx={{ background: "linear-gradient(135deg, #667eea, #764ba2)", textTransform: "none", fontWeight: 700, px: 3 }}
            >
              Send a new link
            </Button>
          )}
          <SafeAuthDebugDetails trace={loadAuthTrace()} />
          <Button
            variant="contained"
            onClick={() => navigate(ROUTES.LOGIN, { replace: true })}
            sx={{
              mt: 1,
              background: "linear-gradient(135deg, #667eea, #764ba2)",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Back to login
          </Button>
        </>
      )}
    </Box>
  );
};

export default AuthCallbackPage;
